import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PageHero } from '../components/PageHero';
import { Wrench, Shield, ArrowRightLeft, Archive, Users, BookOpen, Crosshair, HelpCircle, ArrowRight } from 'lucide-react';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
type ServiceCard = {
  title: string;
  description: string;
  href?: string;
  icon: ComponentType<{
    className?: string;
  }>;
  external?: boolean;
};
export function Services() {
  useDocumentMeta('Services & In-Store Support | Kjergaard Sports', 'Gunsmithing, suppressors and NFA process, firearm transfers, used guns and estates, banquets and fundraisers, Permit to Carry classes, and in-store support at Kjergaard Sports.');
  const services: ServiceCard[] = [{
    title: 'Gunsmithing',
    description: 'Barrel threading, scope mounting, cleaning, repair, stock work, and customization — backed by a recent mill and lathe investment.',
    href: '/gunsmithing',
    icon: Wrench
  }, {
    title: 'Suppressors & NFA Process',
    description: 'In-store help with the ATF Form 4 process, electronic fingerprints, and same-day submission when you arrive prepared.',
    href: '/nfa-process',
    icon: Shield
  }, {
    title: 'Firearm Transfers',
    description: 'Inbound transfers $35, NFA transfers $100. Outbound shipping handled daily, including out-of-state logistics.',
    href: '/firearm-transfers',
    icon: ArrowRightLeft
  }, {
    title: 'Used Guns, Appraisals & Estates',
    description: 'Bring in one gun or a small group anytime. For large collections and estates, we can travel — appraisal fee waived if we make a deal.',
    href: '/used-guns-appraisals-estates',
    icon: Archive
  }, {
    title: 'Banquets & Fundraisers',
    description: 'Fundraising firearm sourcing, winner transfers, and ROI planning for local groups and large national organizations.',
    href: '/banquets-fundraisers',
    icon: Users
  }, {
    title: 'Minnesota Permit to Carry Classes',
    description: 'Saturdays, $100, no signup required. Lance teaches — call or email for the next class date and what to bring.',
    href: '/mn-permit-to-carry-classes',
    icon: BookOpen
  }, {
    title: 'Scope Mounting & Setup Questions',
    description: 'Bring in your rifle and scope. We mount, level, and bore-sight in-store and answer setup questions before you head to the range.',
    icon: Crosshair
  }, {
    title: 'In-Store Product Guidance',
    description: 'Honest, knowledgeable help choosing firearms, optics, ammunition, and gear — without the upsell pressure.',
    icon: HelpCircle
  }];
  return <div data-id="element-1160">
      <PageHero title="Services & In-Store Support" image="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="Gunsmithing workshop at Kjergaard Sports — lathe and shop tools" data-id="element-1161" />

      <section className="py-20 bg-brand-offwhite" data-id="element-1162">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1163">
          {/* Intro */}
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1164">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-1165">
              Real Help from Real People
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-1166">
              Kjergaard Sports is built around in-person service. From
              gunsmithing and suppressor paperwork to firearm transfers, estate
              appraisals, and Permit to Carry classes, the work happens in the
              shop — with people who do it every day.
            </p>
          </div>

          {/* Service cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-1167">
            {services.map(service => {
            const Icon = service.icon;
            const cardBody = <>
                  <Icon className="w-10 h-10 text-brand-hunter mb-5" data-id="element-1168" />
                  <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-1169">
                    {service.title}
                  </h3>
                  <p className="text-brand-charcoal/80 leading-relaxed mb-5 text-[15px]" data-id="element-1170">
                    {service.description}
                  </p>
                  {service.href ? <span className="inline-flex items-center text-brand-leather font-medium group-hover:text-brand-hunter transition-colors" data-id="element-1171">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-1172" />
                    </span> : <span className="inline-flex items-center text-brand-charcoal/50 text-sm italic" data-id="element-1173">
                      Ask in-store
                    </span>}
                </>;
            const cardClasses = 'group block bg-brand-bone p-8 rounded-sm border border-brand-cream h-full hover:border-brand-leather/40 hover:shadow-md transition-all';
            return service.href ? <Link key={service.title} to={service.href} className={cardClasses} data-id="element-1174">
                  {cardBody}
                </Link> : <div key={service.title} className={cardClasses} data-id="element-1175">
                  {cardBody}
                </div>;
          })}
          </div>

          {/* Compliance note */}
          <div className="bg-brand-cream border-l-4 border-brand-leather p-6" data-id="element-1176">
            <p className="text-brand-charcoal/90 text-sm leading-relaxed" data-id="element-1177">
              <strong data-id="element-1178">Note:</strong> All firearm-related sales, transfers, and
              regulated services are handled in-store and in accordance with
              applicable federal, state, and local laws. Information on this
              page is general information only and is not legal advice. Please
              call ahead to confirm availability and current pricing for any
              specific service.
            </p>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-1179" />
    </div>;
}